import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  Hairline,
  StatusPill,
  Tab,
  TabPanel,
  Tabs,
  TabsList,
  TierBadge,
  type Tier,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { CustomerEditForm } from "../components/CustomerEditForm.js";
import { AllowedLocationsEditor } from "../components/AllowedLocationsEditor.js";
import { LifetimeCashbackStat } from "../components/customers/LifetimeCashbackStat.js";
import type {
  CustomerApprovalStatus,
  CustomerDetailDTO,
  CustomerTier,
} from "@wmart/shared-types";

const STATUS_TONE: Record<CustomerApprovalStatus, Tone> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

/**
 * Map the wire CustomerTier ("bronze" | "silver" | "gold" | …) onto the
 * `<TierBadge>` Tier union. The `<TierBadge>` accepts a `tier` whose values
 * align with the canonical tier enum, so this is mostly a passthrough — kept
 * as a function so a future tier rename only changes one site.
 */
function tierToBadge(tier: CustomerTier): Tier {
  return tier as Tier;
}

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const [customer, setCustomer] = useState<CustomerDetailDTO | null>(null);
  const [loadError, setLoadError] = useState(false);

  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<CustomerDetailDTO>(
        `/v1/admin/customers/${id}`,
      );
      setCustomer(data);
      setLoadError(false);
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load customer");
    }
  }, [id, notify, notifyProblem]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (loadError && !customer) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/customers" className="text-body-sm text-accent hover:underline">
          ← Back to customers
        </Link>
        <p className="text-body-sm text-danger">Customer could not be loaded.</p>
      </div>
    );
  }

  if (!customer) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  return (
    <div className="flex flex-col gap-6">
      <Link to="/customers" className="text-body-sm text-accent hover:underline">
        ← Back to customers
      </Link>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="flex min-w-0 flex-col gap-6">
          <Card className="p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <h2 className="font-serif text-display-sm text-ink-900">
                  {customer.email ?? "(no email)"}
                </h2>
                <p className="mt-1 text-body-sm text-ink-500">
                  Customer · {customer.role}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {customer.approval_status === "approved" ? (
                  <TierBadge tier={tierToBadge(customer.tier)} />
                ) : null}
                <StatusPill tone={STATUS_TONE[customer.approval_status]}>
                  {customer.approval_status}
                </StatusPill>
              </div>
            </div>
          </Card>

          <Tabs defaultValue="profile">
            <TabsList aria-label="Customer detail sections">
              <Tab value="profile">Profile</Tab>
              <Tab value="locations">Locations</Tab>
              <Tab value="orders">Orders</Tab>
              <Tab value="events">Events</Tab>
            </TabsList>

            <TabPanel value="profile">
              <Card className="p-6">
                <dl className="grid grid-cols-1 gap-3 text-body-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                      Email
                    </dt>
                    <dd className="mt-1 text-ink-900">{customer.email ?? "(no email)"}</dd>
                  </div>
                  <div>
                    <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                      Role
                    </dt>
                    <dd className="mt-1 text-ink-900">{customer.role}</dd>
                  </div>
                  {customer.approval_status === "approved" ? (
                    <div>
                      <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                        Tier
                      </dt>
                      <dd className="mt-1 text-ink-900">
                        <TierBadge tier={tierToBadge(customer.tier)} />
                      </dd>
                    </div>
                  ) : null}
                  <div>
                    <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                      Company
                    </dt>
                    <dd className="mt-1 text-ink-900 font-mono text-body-sm">
                      {customer.company_id ? `${customer.company_id.slice(0, 8)}…` : "—"}
                    </dd>
                  </div>
                  {customer.approval_status === "rejected" ? (
                    <div className="sm:col-span-2">
                      <dt className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                        Rejection reason
                      </dt>
                      <dd className="mt-1 text-ink-900">
                        {customer.rejection_reason ?? "(no reason recorded)"}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </Card>
            </TabPanel>

            <TabPanel value="locations">
              {customer.approval_status === "approved" ? (
                <Card className="p-6">
                  <AllowedLocationsEditor customer={customer} onSuccess={refetch} />
                </Card>
              ) : (
                <p className="text-body-sm text-ink-500">
                  Locations are configured after approval.
                </p>
              )}
            </TabPanel>

            <TabPanel value="orders">
              <p className="text-body-sm text-ink-500">
                Order history for this customer is available from the{" "}
                <Link to={`/orders?q=${encodeURIComponent(customer.email ?? "")}`} className="text-accent hover:underline">
                  Orders list
                </Link>
                .
              </p>
            </TabPanel>

            <TabPanel value="events">
              <p className="text-body-sm text-ink-500">
                Customer event timeline is on the V1.5 roadmap.
              </p>
            </TabPanel>
          </Tabs>

          {customer.approval_status === "approved" ? (
            <Card className="p-6">
              <h3 className="font-serif text-title-md text-ink-900">Edit details</h3>
              <Hairline spacing="sm" />
              <CustomerEditForm customer={customer} onSuccess={refetch} />
            </Card>
          ) : null}
        </section>

        <aside className="lg:sticky lg:top-24 lg:self-start flex flex-col gap-4">
          <Card className="flex flex-col gap-3 p-6">
            <h3 className="font-serif text-title-md text-ink-900">Actions</h3>

            {customer.approval_status === "pending" ? (
              <p className="text-body-sm text-ink-500">
                Application pending —{" "}
                <Link to="/applications" className="text-accent hover:underline">
                  review in Applications →
                </Link>
              </p>
            ) : null}

            {customer.approval_status === "rejected" ? (
              <p className="text-body-sm text-ink-500">
                This customer was rejected. To reconsider, find the application
                in the{" "}
                <Link to="/applications" className="text-accent hover:underline">
                  Applications queue →
                </Link>
              </p>
            ) : null}

            {customer.approval_status === "approved" ? (
              <p className="text-body-sm text-ink-500">
                Customer is active. Edit details below or manage allowed
                locations in the Locations tab.
              </p>
            ) : null}
          </Card>

          {/* P18-T7: financials sidebar tile — outstanding cashback balance.
              Only render for approved customers; pending/rejected applicants
              have no cashback history to summarise. */}
          {customer.approval_status === "approved" ? (
            <Card className="flex flex-col gap-3 p-6">
              <h3 className="font-serif text-title-md text-ink-900">
                Financials
              </h3>
              <LifetimeCashbackStat customerId={customer.id} />
            </Card>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
